import React, { useState } from 'react';
import { useEventTypes, useCreateServerEvent, useServerEvents, EventType } from '@/hooks/useServerEvents';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Plus, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface EventSelectorProps {
  serverId: string;
  onEventAdded?: () => void;
  isServerPrestige?: boolean; // ✅ NOUVEAU: Vérifier le statut prestige
  isInEditMode?: boolean; // ✅ NOUVEAU: Mode édition ne ferme pas la dialog parente
}

export const EventSelector: React.FC<EventSelectorProps> = ({ serverId, onEventAdded, isServerPrestige = false, isInEditMode = false }) => {
  const { data: eventTypes = [], isLoading } = useEventTypes();
  const { data: serverEvents = [] } = useServerEvents(serverId);
  const createEvent = useCreateServerEvent();
  const { language, t } = useLanguage();
  const { toast } = useToast();
  
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState<EventType | null>(null);
  const [formData, setFormData] = useState<{
    start_date?: Date;
    end_date?: Date;
  }>({});

  const handleEventTypeSelect = (eventType: EventType) => {
    setSelectedEventType(eventType);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Empêcher la propagation vers le dialogue parent
    
    if (!selectedEventType) return;
    
    // Vérifier la limite de 2 événements
    if (serverEvents.length >= 2) {
      toast({
        title: "Limite atteinte",
        description: "Maximum 2 événements par serveur.",
        variant: "destructive",
      });
      return;
    }

    const eventData = {
      server_id: serverId,
      event_type_key: selectedEventType.key,
      title_fr: selectedEventType.name_fr,
      title_en: selectedEventType.name_en,
      title_es: selectedEventType.name_es,
      description_fr: null,
      description_en: null,
      description_es: null,
      start_date: formData.start_date?.toISOString() || null,
      end_date: formData.end_date?.toISOString() || null,
      is_active: true,
      created_by: null,
    };

    try {
      await createEvent.mutateAsync(eventData);
      
      // Reset form et fermer UNIQUEMENT cette dialog
      setFormData({});
      setSelectedEventType(null);
      setIsOpen(false);
      
      toast({
        title: language === 'en' ? 'Event added' : language === 'es' ? 'Evento añadido' : 'Événement ajouté',
        description: language === 'en' ? 'The event has been added successfully' : 
                    language === 'es' ? 'El evento se ha añadido exitosamente' : 
                    'L\'événement a été ajouté avec succès',
      });
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };

  const getEventTypeName = (eventType: EventType) => {
    switch (language) {
      case 'en': return eventType.name_en;
      case 'es': return eventType.name_es;
      default: return eventType.name_fr;
    }
  };

  if (isLoading) {
    return <div className="animate-pulse bg-muted h-10 rounded"></div>;
  }

  // ✅ NOUVEAU: Seuls les serveurs prestige peuvent créer des événements
  if (!isServerPrestige) {
    return (
      <div className="text-center p-4 text-amber-600/80 bg-amber-500/10 rounded-lg border border-amber-500/20">
        <Sparkles className="w-5 h-5 mx-auto mb-2 text-amber-500" />
        <p className="text-sm font-medium">
          {language === 'en' ? 'Events are exclusive to Prestige servers' : 
           language === 'es' ? 'Los eventos son exclusivos de servidores Prestige' : 
           'Les événements sont exclusifs aux serveurs Prestige'}
        </p>
      </div>
    );
  }

  const canAddMoreEvents = serverEvents.length < 2;

  if (!canAddMoreEvents) {
    return (
      <div className="text-center p-4 text-muted-foreground">
        <p className="text-sm">
          {language === 'en' ? 'Limit reached: 2 events maximum per server' : 
           language === 'es' ? 'Límite alcanzado: 2 eventos máximo por servidor' : 
           'Limite atteinte : 2 événements maximum par serveur'}
        </p>
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen} modal={false}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 retro-button">
          <Plus className="w-4 h-4" />
          <Sparkles className="w-4 h-4 text-yellow-400" />
          {language === 'en' ? 'Add an event' : 
           language === 'es' ? 'Añadir un evento' : 
           'Ajouter un événement'}
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onEscapeKeyDown={(e) => e.stopPropagation()} onInteractOutside={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-400" />
            {language === 'en' ? 'Add a server event' : 
             language === 'es' ? 'Añadir un evento del servidor' : 
             'Ajouter un événement serveur'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Sélection du type d'événement */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">
              {language === 'en' ? 'Event type' : 
               language === 'es' ? 'Tipo de evento' : 
               'Type d\'événement'}
            </Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {eventTypes.map((eventType) => (
                <button
                  key={eventType.id}
                  type="button"
                  onClick={() => handleEventTypeSelect(eventType)}
                  className={cn(
                    "p-3 rounded-lg border-2 transition-all duration-200 text-left",
                    "hover:scale-105 hover:shadow-lg retro-glow",
                    selectedEventType?.id === eventType.id
                      ? "border-primary bg-primary/10 shadow-lg animate-pulse"
                      : "border-border hover:border-primary/50"
                  )}
                  style={{
                    borderColor: selectedEventType?.id === eventType.id ? eventType.color : undefined
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{eventType.icon}</span>
                    <span 
                      className="w-3 h-3 rounded-full animate-pulse" 
                      style={{ backgroundColor: eventType.color }}
                    ></span>
                  </div>
                  <div className="font-medium text-sm">
                    {getEventTypeName(eventType)}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {selectedEventType && (
            <div className="space-y-4 border-t pt-4">
              {/* Aperçu de l'événement sélectionné */}
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-lg"
                    style={{ backgroundColor: selectedEventType.color + '20' }}
                  >
                    {selectedEventType.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold">{getEventTypeName(selectedEventType)}</h4>
                    <p className="text-sm text-muted-foreground">
                      {language === 'en' ? 'Predefined event' : 
                       language === 'es' ? 'Evento predefinido' : 
                       'Événement prédéfini'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>
                    {language === 'en' ? 'Start date (optional)' : 
                     language === 'es' ? 'Fecha de inicio (opcional)' : 
                     'Date de début (optionnel)'}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.start_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.start_date ? format(formData.start_date, "PPP") : 
                         (language === 'en' ? 'Select a date' : 
                          language === 'es' ? 'Seleccionar una fecha' : 
                          'Sélectionner une date')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.start_date}
                        onSelect={(date) => setFormData(prev => ({ ...prev, start_date: date }))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div>
                  <Label>
                    {language === 'en' ? 'End date (optional)' : 
                     language === 'es' ? 'Fecha de fin (opcional)' : 
                     'Date de fin (optionnel)'}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.end_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.end_date ? format(formData.end_date, "PPP") : 
                         (language === 'en' ? 'Select a date' : 
                          language === 'es' ? 'Seleccionar una fecha' : 
                          'Sélectionner une date')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.end_date}
                        onSelect={(date) => setFormData(prev => ({ ...prev, end_date: date }))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button 
              type="submit" 
              disabled={!selectedEventType || createEvent.isPending}
              className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
              style={{
                background: 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary)) 100%)',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              }}
            >
              {createEvent.isPending ? 
                (language === 'en' ? 'Creating...' : language === 'es' ? 'Creando...' : 'Création...') : 
                (language === 'en' ? 'Create event' : language === 'es' ? 'Crear evento' : 'Créer l\'événement')}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setIsOpen(false)}
            >
              {language === 'en' ? 'Cancel' : language === 'es' ? 'Cancelar' : 'Annuler'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};